import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CoachAvatar } from "@/components/ui/CoachAvatar";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const cancelled = location.pathname.includes("cancel");
  const [status, setStatus] = useState(cancelled ? "cancelled" : "checking");

  useEffect(() => {
    if (cancelled) return;
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      tries++;
      try {
        const { data } = await api.get(`/api/payments/status/${sid}`);
        if (data.payment_status === "paid") { setStatus("paid"); refresh(); return; }
        if (data.status === "expired" || tries > 10) { setStatus("error"); return; }
      } catch (_) { if (tries > 10) { setStatus("error"); return; } }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const map = {
    checking: { icon: <Loader2 className="w-10 h-10 text-acid animate-spin" />, title: "Confirming payment…", body: "Hold tight while we sync with Stripe." },
    paid: { icon: <CheckCircle2 className="w-12 h-12 text-acid" />, title: "Membership active", body: "Welcome to the full OCTON Gym. Your coach is ready when you are." },
    cancelled: { icon: <XCircle className="w-12 h-12 text-[#8A91A6]" />, title: "Checkout cancelled", body: "No charge was made. You can pick a plan any time." },
    error: { icon: <XCircle className="w-12 h-12 text-pulseError" />, title: "Something went wrong", body: "We couldn't confirm your payment. Try again." },
  }[status];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-6 text-center">
      <CoachAvatar size={140} />
      <div className="flex flex-col items-center gap-3">
        {map.icon}
        <h1 className="font-heading font-black text-3xl tracking-tight">{map.title}</h1>
        <p className="text-[#8A91A6] max-w-sm">{map.body}</p>
      </div>
      <div className="flex gap-3">
        <Button onClick={() => navigate("/app")} className="bg-acid text-black hover:bg-[#AACC00] rounded-full h-11 px-6" data-testid="payment-dashboard-btn">Go to dashboard</Button>
        {status !== "paid" && <Button onClick={() => navigate("/app/membership")} variant="outline" className="rounded-full h-11 px-6 border-[#343A4A] bg-transparent hover:bg-white/5">Back to plans</Button>}
      </div>
    </div>
  );
}
